import React, { useState, useMemo } from 'react';
import { Plus, Trash2, ChevronRight, ChevronDown, Package, FolderOpen, Tag, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminProductsTab = ({ products, fetchData, authHeaders, showMsg, loading, setLoading, UploadButton, handleFormImageUpload }) => {
  const [view, setView] = useState('categories'); // 'categories' | 'subcategories' | 'products'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');
  const [productForm, setProductForm] = useState({ name: '', price: '', image: '', brand: '', description: '' });
  const [expandedCats, setExpandedCats] = useState({});

  // Derive categories and subcategories from products
  const categories = useMemo(() => {
    const catMap = {};
    products.forEach(p => {
      if (!catMap[p.category]) catMap[p.category] = { subcategories: new Set(), count: 0 };
      catMap[p.category].count++;
      if (p.subcategory) catMap[p.category].subcategories.add(p.subcategory);
    });
    return Object.entries(catMap).map(([name, data]) => ({
      name,
      subcategories: [...data.subcategories],
      count: data.count
    }));
  }, [products]);

  const subcategories = useMemo(() => {
    if (!selectedCategory) return [];
    const subs = {};
    products.filter(p => p.category === selectedCategory).forEach(p => {
      const sub = p.subcategory || 'Uncategorized';
      if (!subs[sub]) subs[sub] = 0;
      subs[sub]++;
    });
    return Object.entries(subs).map(([name, count]) => ({ name, count }));
  }, [products, selectedCategory]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (selectedCategory && p.category !== selectedCategory) return false;
      if (selectedSubcategory && p.subcategory !== selectedSubcategory) return false;
      return true;
    });
  }, [products, selectedCategory, selectedSubcategory]);

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/admin/products/${id}`, { method: 'DELETE', headers: authHeaders() });
      fetchData(); showMsg('Product deleted');
    } catch { showMsg('Delete failed', 'error'); }
    setLoading(false);
  };

  const addProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        ...productForm,
        price: parseFloat(productForm.price),
        category: selectedCategory,
        subcategory: selectedSubcategory || ''
      };
      const res = await fetch(`${API}/api/admin/products`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      setProductForm({ name: '', price: '', image: '', brand: '', description: '' });
      fetchData(); showMsg('Product added!');
    } catch (err) { showMsg(err.message, 'error'); }
    setLoading(false);
  };

  const addCategoryProduct = async (catName) => {
    // Create a placeholder product to establish the category
    setSelectedCategory(catName);
    setSelectedSubcategory('');
    setView('subcategories');
  };

  const seedProducts = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/admin/products/seed`, { method: 'POST', headers: authHeaders() });
      fetchData(); showMsg('Default products loaded!');
    } catch { showMsg('Seed failed', 'error'); }
    setLoading(false);
  };

  // Empty state
  if (products.length === 0) {
    return (
      <div className="text-center py-16" data-testid="no-products">
        <Package size={48} className="mx-auto text-[#D4A574] mb-4" />
        <p className="text-lg text-[#2C1810] font-semibold mb-2">No products in database</p>
        <p className="text-sm text-[#7a6555] mb-6">Load default products or start adding categories</p>
        <Button onClick={seedProducts} disabled={loading} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer" data-testid="seed-products-btn">
          {loading ? 'Loading...' : 'Load Default Products'}
        </Button>
      </div>
    );
  }

  // ===== PRODUCTS VIEW (deepest level) =====
  if (view === 'products') {
    return (
      <div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-5 text-sm">
          <button onClick={() => { setView('categories'); setSelectedCategory(''); setSelectedSubcategory(''); }} className="text-[#5C7A3A] hover:underline cursor-pointer font-medium" data-testid="breadcrumb-categories">All Categories</button>
          <ChevronRight size={14} className="text-[#7a6555]" />
          <button onClick={() => { setView('subcategories'); setSelectedSubcategory(''); }} className="text-[#5C7A3A] hover:underline cursor-pointer font-medium" data-testid="breadcrumb-category">{selectedCategory}</button>
          {selectedSubcategory && (
            <>
              <ChevronRight size={14} className="text-[#7a6555]" />
              <span className="text-[#2C1810] font-semibold">{selectedSubcategory}</span>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Add Product Form */}
          <Card className="lg:col-span-1 shadow-sm border border-[#E8DFD3] h-fit bg-white">
            <CardContent className="p-5">
              <h3 className="font-bold text-[#2C1810] mb-1 flex items-center gap-2">
                <Plus size={16} className="text-[#5C7A3A]" /> Add Product
              </h3>
              <p className="text-xs text-[#7a6555] mb-4">
                in {selectedCategory}{selectedSubcategory ? ` / ${selectedSubcategory}` : ''}
              </p>
              <form onSubmit={addProduct} className="space-y-3">
                <Input data-testid="product-name-input" placeholder="Product name" value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))} required />
                <Input data-testid="product-price-input" type="number" placeholder="Price (Rs)" value={productForm.price} onChange={e => setProductForm(f => ({ ...f, price: e.target.value }))} required />
                <UploadButton currentUrl={productForm.image} onUpload={(file) => handleFormImageUpload(file, setProductForm, 'image')} label="Upload Product Image" />
                <div>
                  <label className="text-xs font-medium text-[#7a6555] mb-1 block">Brand</label>
                  <Input data-testid="product-brand-input" list="brands-list" placeholder="Brand name" value={productForm.brand} onChange={e => setProductForm(f => ({ ...f, brand: e.target.value }))} required />
                  <datalist id="brands-list">
                    {[...new Set(products.map(p => p.brand).filter(Boolean))].map(b => <option key={b} value={b} />)}
                  </datalist>
                </div>
                <Textarea data-testid="product-desc-input" placeholder="Description" value={productForm.description} onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))} required />
                <Button type="submit" data-testid="add-product-btn" disabled={loading || !productForm.image} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">
                  {loading ? 'Adding...' : 'Add Product'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Product List */}
          <div className="lg:col-span-2 space-y-3">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 text-[#7a6555]">No products in this {selectedSubcategory ? 'subcategory' : 'category'} yet.</div>
            ) : (
              filteredProducts.map(item => (
                <Card key={item.id} className="shadow-sm border border-[#E8DFD3] bg-white" data-testid={`product-item-${item.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#2C1810] truncate">{item.name}</p>
                      <p className="text-xs text-[#7a6555]">{item.brand}</p>
                      <p className="text-sm text-[#5C7A3A] font-semibold">Rs. {item.price}</p>
                    </div>
                    <Button onClick={() => deleteProduct(item.id)} variant="ghost" size="sm" data-testid={`delete-product-${item.id}`} className="text-red-500 hover:bg-red-50 cursor-pointer">
                      <Trash2 size={16} />
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== SUBCATEGORIES VIEW (middle level) =====
  if (view === 'subcategories') {
    return (
      <div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-5 text-sm">
          <button onClick={() => { setView('categories'); setSelectedCategory(''); }} className="text-[#5C7A3A] hover:underline cursor-pointer font-medium" data-testid="breadcrumb-categories">All Categories</button>
          <ChevronRight size={14} className="text-[#7a6555]" />
          <span className="text-[#2C1810] font-semibold">{selectedCategory}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Add Subcategory */}
          <Card className="lg:col-span-1 shadow-sm border border-[#E8DFD3] h-fit bg-white">
            <CardContent className="p-5">
              <h3 className="font-bold text-[#2C1810] mb-4 flex items-center gap-2">
                <Plus size={16} className="text-[#5C7A3A]" /> Add Subcategory
              </h3>
              <p className="text-xs text-[#7a6555] mb-3">in {selectedCategory}</p>
              <div className="flex gap-2">
                <Input
                  data-testid="new-subcategory-input"
                  placeholder="e.g., Biryani, Rice Bowl"
                  value={newSubcategory}
                  onChange={e => setNewSubcategory(e.target.value)}
                />
                <Button
                  onClick={() => {
                    if (newSubcategory.trim()) {
                      setSelectedSubcategory(newSubcategory.trim());
                      setNewSubcategory('');
                      setView('products');
                    }
                  }}
                  disabled={!newSubcategory.trim()}
                  className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer flex-shrink-0"
                  data-testid="add-subcategory-btn"
                >
                  <Plus size={16} />
                </Button>
              </div>
              <p className="text-xs text-[#7a6555] mt-3">Or click an existing subcategory to manage products</p>

              <div className="mt-5 pt-4 border-t border-[#E8DFD3]">
                <Button
                  onClick={() => { setSelectedSubcategory(''); setView('products'); }}
                  variant="outline"
                  className="w-full border-[#D4A574] text-[#C4844A] hover:bg-[#C4844A]/5 cursor-pointer"
                  data-testid="add-product-no-subcat-btn"
                >
                  <Plus size={14} className="mr-2" /> Add Product Directly
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Subcategory List */}
          <div className="lg:col-span-2 space-y-3">
            {subcategories.length === 0 ? (
              <div className="text-center py-12 text-[#7a6555]">No subcategories yet. Add one to get started.</div>
            ) : (
              subcategories.map(sub => (
                <Card
                  key={sub.name}
                  className="shadow-sm border border-[#E8DFD3] bg-white hover:shadow-md hover:border-[#D4A574]/40 transition-all cursor-pointer"
                  data-testid={`subcategory-card-${sub.name}`}
                  onClick={() => { setSelectedSubcategory(sub.name); setView('products'); }}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#C4844A]/10 flex items-center justify-center flex-shrink-0">
                      <Tag size={20} className="text-[#C4844A]" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-[#2C1810]">{sub.name}</p>
                      <p className="text-xs text-[#7a6555]">{sub.count} product{sub.count !== 1 ? 's' : ''}</p>
                    </div>
                    <ChevronRight size={18} className="text-[#7a6555]" />
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== CATEGORIES VIEW (top level - default) =====
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Category */}
        <Card className="lg:col-span-1 shadow-sm border border-[#E8DFD3] h-fit bg-white">
          <CardContent className="p-5">
            <h3 className="font-bold text-[#2C1810] mb-4 flex items-center gap-2">
              <Plus size={16} className="text-[#5C7A3A]" /> Add Category
            </h3>
            <div className="flex gap-2">
              <Input
                data-testid="new-category-input"
                placeholder="e.g., Biryani & Rice"
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
              />
              <Button
                onClick={() => {
                  if (newCategory.trim()) {
                    setSelectedCategory(newCategory.trim());
                    setNewCategory('');
                    setView('subcategories');
                  }
                }}
                disabled={!newCategory.trim()}
                className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer flex-shrink-0"
                data-testid="add-category-btn"
              >
                <Plus size={16} />
              </Button>
            </div>
            <p className="text-xs text-[#7a6555] mt-3">Or click an existing category to manage subcategories & products</p>

            <div className="mt-5 pt-4 border-t border-[#E8DFD3]">
              <p className="text-xs font-semibold text-[#2C1810] mb-2">Summary</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#FDF8F0] rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-[#5C7A3A]">{categories.length}</p>
                  <p className="text-xs text-[#7a6555]">Categories</p>
                </div>
                <div className="bg-[#FDF8F0] rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-[#C4844A]">{products.length}</p>
                  <p className="text-xs text-[#7a6555]">Products</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category List */}
        <div className="lg:col-span-2 space-y-3">
          {categories.map(cat => (
            <Card
              key={cat.name}
              className="shadow-sm border border-[#E8DFD3] bg-white hover:shadow-md hover:border-[#5C7A3A]/30 transition-all"
              data-testid={`category-card-${cat.name}`}
            >
              <CardContent className="p-4">
                <div
                  className="flex items-center gap-4 cursor-pointer"
                  onClick={() => { setSelectedCategory(cat.name); setView('subcategories'); }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#5C7A3A]/10 flex items-center justify-center flex-shrink-0">
                    <FolderOpen size={20} className="text-[#5C7A3A]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#2C1810]">{cat.name}</p>
                    <p className="text-xs text-[#7a6555]">
                      {cat.subcategories.length} subcategor{cat.subcategories.length !== 1 ? 'ies' : 'y'} &middot; {cat.count} product{cat.count !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setExpandedCats(prev => ({ ...prev, [cat.name]: !prev[cat.name] })); }}
                    className="p-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                    data-testid={`toggle-category-${cat.name}`}
                  >
                    {expandedCats[cat.name] ? <ChevronDown size={18} className="text-[#7a6555]" /> : <ChevronRight size={18} className="text-[#7a6555]" />}
                  </button>
                </div>

                {/* Expanded: show subcategories inline */}
                {expandedCats[cat.name] && (
                  <div className="mt-3 ml-16 space-y-1.5 border-l-2 border-[#E8DFD3] pl-4">
                    {cat.subcategories.map(sub => {
                      const subCount = products.filter(p => p.category === cat.name && p.subcategory === sub).length;
                      return (
                        <button
                          key={sub}
                          onClick={() => { setSelectedCategory(cat.name); setSelectedSubcategory(sub); setView('products'); }}
                          className="w-full text-left flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FDF8F0] transition-colors cursor-pointer group"
                          data-testid={`subcat-link-${sub}`}
                        >
                          <div className="flex items-center gap-2">
                            <Tag size={13} className="text-[#C4844A]" />
                            <span className="text-sm text-[#2C1810] group-hover:text-[#5C7A3A] transition-colors">{sub}</span>
                          </div>
                          <span className="text-xs text-[#7a6555]">{subCount}</span>
                        </button>
                      );
                    })}
                    {cat.subcategories.length === 0 && (
                      <p className="text-xs text-[#7a6555] py-2">No subcategories</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminProductsTab;
